import React, { useEffect, useState, useMemo } from 'react'
import { values, sortBy } from 'lodash-es'
import { connect, ConnectedProps } from 'react-redux'
import { bindActionCreators } from 'redux'
import * as filtersActions from 'syft-acp-core/store/filters/actions'
import * as countriesActions from 'syft-acp-core/actions/countries'
import { Store } from 'syft-acp-core/store'

import FilterHierarchyMultiselect from '../FilterHierarchyMultiselect'
import { Props, Option, City } from './types'

type AllProps = ConnectedProps<typeof storeConnector> & Props

const getCityOptions = (citiesMap: Record<string, City>): Option[] =>
  values(sortBy(citiesMap, ['order_number', 'name'])).map(city => ({
    value: String(city.id),
    label: city.name,
  }))

// Legacy parameter name for backwards compatibility with bookmarked URLs
const LEGACY_PARAM_NAME = 'city_id'

const getInitialValues = (query: Record<string, string | string[]>, name: string): Option[] => {
  // Read from new plural param (city_ids) and legacy singular param (city_id)
  const newParamValue = query[name]
  const legacyParamValue = query[LEGACY_PARAM_NAME]

  // Parse new param values
  const newIds = newParamValue
    ? Array.isArray(newParamValue)
      ? newParamValue
      : newParamValue.split(',')
    : []

  // Parse legacy param value (single ID or comma-separated)
  const legacyIds = legacyParamValue
    ? Array.isArray(legacyParamValue)
      ? legacyParamValue
      : legacyParamValue.split(',')
    : []

  // Merge and deduplicate IDs (legacy first for consistency with backend)
  const allIds = [...new Set([...legacyIds, ...newIds])]

  return allIds.map(id => ({ value: String(id), label: '' }))
}

export const storeConnector = connect(
  (state: Store) => ({
    cities: state.cities.entityMap,
    hasCitiesData: state.cities.hasData,
    query: state.routing?.locationBeforeTransitions?.query || {},
  }),
  dispatch => ({
    actions: bindActionCreators({ ...filtersActions, ...countriesActions }, dispatch),
  }),
)

const FilterSelectCitiesMultiselect = ({
  cities,
  hasCitiesData,
  actions,
  name,
  query,
  onChange,
}: AllProps) => {
  const cityOptions = useMemo(() => getCityOptions(cities || {}), [cities])

  // Get initial values from URL query params
  const initialValues = useMemo(() => {
    const queryValues = getInitialValues(query, name)
    // Map to include labels from cityOptions
    return queryValues
      .map(qv => {
        const option = cityOptions.find(opt => opt.value === qv.value)
        return option || null
      })
      .filter((opt): opt is Option => opt !== null)
  }, [query, name, cityOptions])

  const [selected, setSelected] = useState<Option[]>(initialValues)
  const [userHasInteracted, setUserHasInteracted] = useState(false)

  // Fetch cities data on mount if not already loaded
  useEffect(() => {
    if (!hasCitiesData) {
      actions.fetchCountries()
    }
  }, [hasCitiesData, actions])

  // Update selected when initialValues change (e.g., URL changes)
  // Only sync from URL when user hasn't interacted AND current selection is empty
  useEffect(() => {
    if (!userHasInteracted && initialValues.length > 0 && selected.length === 0) {
      setSelected(initialValues)
    }
  }, [initialValues, userHasInteracted, selected.length])

  const onChangeHandler = (val: Option[]) => {
    if (!userHasInteracted) {
      setUserHasInteracted(true)
    }
    setSelected(val)
    const ids = val.map(item => item.value)
    // Send array of IDs to the filter
    const filterValue = ids.length > 0 ? ids : null
    actions.setFilter(name, filterValue)
    onChange?.(name, filterValue || undefined)
  }

  return (
    <FilterHierarchyMultiselect
      values={selected}
      onChange={onChangeHandler}
      options={cityOptions}
      placeholder="Any"
      deselectOption
      scrollable
      refreshValues={false}
    />
  )
}

export default storeConnector(FilterSelectCitiesMultiselect)
